import { Participant, Track } from 'livekit-client';

export interface RtcMediaStatsData {
  videoKbps?: number;
  audioKbps?: number;
  /** Instantaneous video bitrate in bits per second (from delta / interval when prev exists) */
  videoBitrateBps?: number;
  audioBitrateBps?: number;
  videoPacketLoss?: number;
  audioPacketLoss?: number;
  /** RTP jitter in seconds (Chrome/Firefox expose on inbound-rtp / sometimes outbound-rtp) */
  videoJitterSec?: number;
  audioJitterSec?: number;
  /** Chrome inbound-rtp: freeze count / duration (viewer freezes). */
  freezeCount?: number;
  totalFreezesDurationSec?: number;
  width?: number;
  height?: number;
  fps?: number;
  audioCodec?: string;
  videoCodec?: string;
}

export interface RtcStatsPrevEntry {
  bytes: number;
  packetsLost: number;
  packetsTotal: number;
  framesDecoded: number;
  /** RTP stats clock (ms) from the last `getStats` sample */
  timestamp: number;
  /** `performance.now()` when `bytes` was sampled — used for bitrate (matches telemetry interval). */
  wallMs: number;
}

function parseCodec(mimeType: string | undefined): string {
  if (!mimeType) return '';
  const slash = mimeType.lastIndexOf('/');
  return slash >= 0 ? mimeType.substring(slash + 1) : mimeType;
}

/**
 * Bitrate / packet loss from LiveKit track RTC stats (same logic as the connection tooltip).
 * Uses prev refs to compute deltas between polls.
 * @param includeAudio When false, only the video branch for `videoSource` runs (e.g. screen share without re-counting mic).
 */
export async function fetchTrackStats(
  participant: Participant,
  videoSource: Track.Source,
  prevVideoRef: { current: RtcStatsPrevEntry | null },
  prevAudioRef: { current: RtcStatsPrevEntry | null },
  includeAudio: boolean = true,
): Promise<RtcMediaStatsData> {
  const isLocal = participant.isLocal;
  const rtpType = isLocal ? 'outbound-rtp' : 'inbound-rtp';
  const newStats: RtcMediaStatsData = {};

  const videoPub = participant.getTrackPublication(videoSource);
  const videoTrack = videoPub?.track as { getRTCStatsReport?: () => Promise<RTCStatsReport> } | undefined;
  if (videoTrack?.getRTCStatsReport) {
    try {
      const report: RTCStatsReport = await videoTrack.getRTCStatsReport();
      let rtpEntry: Record<string, unknown> | null = null;
      let codecEntry: Record<string, unknown> | null = null;

      // Some browsers expose multiple video RTP entries; pick the one moving the most bytes.
      let bestVideoBytes = -1;
      report.forEach((entry) => {
        const e = entry as Record<string, unknown>;
        if (e.type !== rtpType || e.kind !== 'video') return;
        const re = e as { bytesSent?: number; bytesReceived?: number };
        const b = isLocal ? (re.bytesSent ?? 0) : (re.bytesReceived ?? 0);
        if (b >= bestVideoBytes) {
          bestVideoBytes = b;
          rtpEntry = e;
        }
      });

      if (rtpEntry && (rtpEntry as { codecId?: string }).codecId) {
        codecEntry = report.get((rtpEntry as { codecId: string }).codecId) as Record<string, unknown>;
      }
      if (!codecEntry) {
        report.forEach((entry) => {
          const e = entry as Record<string, unknown>;
          if (e.type === 'codec' && String(e.mimeType ?? '')
            .toLowerCase()
            .startsWith('video/')) {
            codecEntry = e;
          }
        });
      }

      if (rtpEntry) {
        const re = rtpEntry as {
          timestamp: number;
          bytesSent?: number;
          bytesReceived?: number;
          frameWidth?: number;
          frameHeight?: number;
          framesPerSecond?: number;
          framesDecoded?: number;
          packetsLost?: number;
          packetsSent?: number;
          packetsReceived?: number;
          freezeCount?: number;
          totalFreezesDuration?: number;
        };
        newStats.width = re.frameWidth;
        newStats.height = re.frameHeight;

        if (re.framesPerSecond !== undefined) {
          newStats.fps = Math.round(re.framesPerSecond);
        }
        if (typeof re.freezeCount === 'number' && Number.isFinite(re.freezeCount)) {
          newStats.freezeCount = re.freezeCount;
        }
        if (
          typeof re.totalFreezesDuration === 'number' &&
          Number.isFinite(re.totalFreezesDuration)
        ) {
          newStats.totalFreezesDurationSec = re.totalFreezesDuration;
        }

        const now = re.timestamp;
        const bytes = isLocal ? (re.bytesSent ?? 0) : (re.bytesReceived ?? 0);
        const framesDecoded = re.framesDecoded ?? 0;
        const packetsLost = re.packetsLost ?? 0;
        const packetsTotal = isLocal
          ? packetsLost + (re.packetsSent ?? 0)
          : packetsLost + (re.packetsReceived ?? 0);

        const jitterRaw = (re as { jitter?: number }).jitter;
        if (typeof jitterRaw === 'number' && Number.isFinite(jitterRaw) && jitterRaw >= 0) {
          newStats.videoJitterSec = jitterRaw;
        }

        const wallNow = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const prev = prevVideoRef.current;
        if (prev) {
          const wallDiff = wallNow - prev.wallMs;
          if (wallDiff >= 150 && bytes >= prev.bytes) {
            newStats.videoKbps = Math.max(0, Math.round(((bytes - prev.bytes) * 8) / wallDiff));
            newStats.videoBitrateBps = Math.max(0, Math.round(((bytes - prev.bytes) * 8 * 1000) / wallDiff));
          }
          if (now > prev.timestamp) {
            const timeDiff = now - prev.timestamp;
            if (newStats.fps === undefined && timeDiff > 0) {
              const framesDiff = framesDecoded - prev.framesDecoded;
              newStats.fps = Math.round((framesDiff / timeDiff) * 1000);
            }
          }
        }

        newStats.videoPacketLoss =
          packetsTotal > 0 ? Math.round((packetsLost / packetsTotal) * 100) : 0;
        prevVideoRef.current = {
          bytes,
          packetsLost,
          packetsTotal,
          framesDecoded,
          timestamp: now,
          wallMs: wallNow,
        };
      }

      if (codecEntry) {
        newStats.videoCodec = parseCodec(codecEntry.mimeType as string | undefined);
      }
    } catch {
      // ignore
    }
  }

  if (!includeAudio) {
    return newStats;
  }

  const audioPub = participant.getTrackPublication(Track.Source.Microphone);
  const audioTrack = audioPub?.track as { getRTCStatsReport?: () => Promise<RTCStatsReport> } | undefined;
  if (audioTrack?.getRTCStatsReport) {
    try {
      const report: RTCStatsReport = await audioTrack.getRTCStatsReport();
      let rtpEntry: Record<string, unknown> | null = null;
      let codecEntry: Record<string, unknown> | null = null;

      let bestAudioBytes = -1;
      report.forEach((entry) => {
        const e = entry as Record<string, unknown>;
        if (e.type !== rtpType || e.kind !== 'audio') return;
        const re = e as { bytesSent?: number; bytesReceived?: number };
        const b = isLocal ? (re.bytesSent ?? 0) : (re.bytesReceived ?? 0);
        if (b >= bestAudioBytes) {
          bestAudioBytes = b;
          rtpEntry = e;
        }
      });

      if (rtpEntry && (rtpEntry as { codecId?: string }).codecId) {
        codecEntry = report.get((rtpEntry as { codecId: string }).codecId) as Record<string, unknown>;
      }
      if (!codecEntry) {
        report.forEach((entry) => {
          const e = entry as Record<string, unknown>;
          if (e.type === 'codec' && String(e.mimeType ?? '')
            .toLowerCase()
            .startsWith('audio/')) {
            codecEntry = e;
          }
        });
      }

      if (rtpEntry) {
        const re = rtpEntry as {
          timestamp: number;
          bytesSent?: number;
          bytesReceived?: number;
          packetsLost?: number;
          packetsSent?: number;
          packetsReceived?: number;
        };
        const now = re.timestamp;
        const bytes = isLocal ? (re.bytesSent ?? 0) : (re.bytesReceived ?? 0);
        const packetsLost = re.packetsLost ?? 0;
        const packetsTotal = isLocal
          ? packetsLost + (re.packetsSent ?? 0)
          : packetsLost + (re.packetsReceived ?? 0);

        const jitterRaw = (re as { jitter?: number }).jitter;
        if (typeof jitterRaw === 'number' && Number.isFinite(jitterRaw) && jitterRaw >= 0) {
          newStats.audioJitterSec = jitterRaw;
        }

        const wallNow = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const prev = prevAudioRef.current;
        if (prev && wallNow - prev.wallMs >= 150 && bytes >= prev.bytes) {
          const wallDiff = wallNow - prev.wallMs;
          newStats.audioKbps = Math.max(0, Math.round(((bytes - prev.bytes) * 8) / wallDiff));
          newStats.audioBitrateBps = Math.max(0, Math.round(((bytes - prev.bytes) * 8 * 1000) / wallDiff));
        }

        newStats.audioPacketLoss =
          packetsTotal > 0 ? Math.round((packetsLost / packetsTotal) * 100) : 0;
        prevAudioRef.current = {
          bytes,
          packetsLost,
          packetsTotal,
          framesDecoded: 0,
          timestamp: now,
          wallMs: wallNow,
        };
      }

      if (codecEntry) {
        newStats.audioCodec = parseCodec(codecEntry.mimeType as string | undefined);
      }
    } catch {
      // ignore
    }
  }

  return newStats;
}
